/**
 * INTERIM list-read for the Experiences overview grid.
 *
 * The overview only needs a handful of fields per template (title, status,
 * ai-badge, sort key), but `experienceTemplatesV1` docs are the full authoring
 * payload — tasks + messages + resources — averaging ~650 KB each. Listening to
 * the whole collection therefore streams ~100 MB on a heavy org (166 templates),
 * which is what gates the grid behind the spinner for 10–15 s.
 *
 * The Firestore Web SDK cannot project fields (no `.select()`), but the REST API
 * can (`structuredQuery.select`). So this does a ONE-SHOT authenticated
 * `runQuery` with a field mask, returning ~250 bytes per template (~35 KB total)
 * instead of ~100 MB. Drop-in for `useExperienceTemplatesByOrgId` on the read
 * side; the shape returned is a `Doc<IExperienceTemplate>` carrying only the
 * masked fields.
 *
 * Trade-off vs the live listener it replaces: this is a one-shot fetch, so the
 * grid does not live-update on template create/edit/status-change until the page
 * is reloaded. Acceptable for a list surface; the durable fix is a maintained
 * `experienceTemplatesMinimalV1` projection (which this hook will then simply
 * read via the SDK instead of REST).
 *
 * Actions that need the full template (Duplicate) lazy-load it by id on click
 * via `getExperienceTemplateById`; Stop/Archive only need the doc ref
 * (collection + id), which the minimal doc carries.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import { FS } from '@dadohr/shared/versions';
import firebase from 'firebase/app';
import { useCallback, useEffect, useRef, useState } from 'react';

import { auth, LoadingValue, LoadingValueExtended } from '../firebase';

// The only fields the overview grid reads off each template.
const SELECT_FIELDS = [
  'orgId',
  'title',
  'adminTitle',
  'status',
  'aiFeatureUsed',
  'updatedAt',
  'expectedEndThreshold'
] as const;

// Firestore REST wraps every value in a type tag; unwrap the few types we mask.
const fromRestValue = (v: any): any => {
  if (v == null) return undefined;
  if ('stringValue' in v) return v.stringValue;
  if ('booleanValue' in v) return v.booleanValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return v.doubleValue;
  if ('timestampValue' in v) {
    // RFC3339 string → client Timestamp so `.toMillis()` (sort key) works.
    return firebase.firestore.Timestamp.fromDate(new Date(v.timestampValue));
  }
  if ('nullValue' in v) return null;
  return undefined;
};

const restDocToTemplateDoc = (doc: any): Doc<IExperienceTemplate> => {
  const id = String(doc.name).split('/').pop() as string;
  const fields = doc.fields || {};
  const data: any = { id };
  SELECT_FIELDS.forEach((k) => {
    if (fields[k] !== undefined) data[k] = fromRestValue(fields[k]);
  });
  // collection is required so updateDoc(tpl, …) (Stop/Archive) resolves a ref.
  return { id, collection: FS.experienceTemplates, data } as Doc<IExperienceTemplate>;
};

/**
 * One-shot, field-masked fetch of an org's templates via the Firestore REST API.
 * Returns partially-populated template docs (only SELECT_FIELDS + id).
 */
export const fetchExperienceTemplatesMinimalByOrgId = async (
  orgId: string
): Promise<Doc<IExperienceTemplate>[]> => {
  const user = auth().currentUser;
  if (!user) throw new Error('NOT_AUTHENTICATED');
  const token = await user.getIdToken();
  const projectId = (firebase.app().options as { projectId?: string })
    .projectId;
  const url = `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents:runQuery`;
  const body = {
    structuredQuery: {
      from: [{ collectionId: FS.experienceTemplates }],
      where: {
        fieldFilter: {
          field: { fieldPath: 'orgId' },
          op: 'EQUAL',
          value: { stringValue: orgId }
        }
      },
      select: { fields: SELECT_FIELDS.map((fieldPath) => ({ fieldPath })) }
    }
  };
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  if (!resp.ok) {
    throw new Error(`runQuery failed: ${resp.status} ${await resp.text()}`);
  }
  const rows = await resp.json();
  return (Array.isArray(rows) ? rows : [])
    .filter((r: any) => r && r.document)
    .map((r: any) => restDocToTemplateDoc(r.document));
};

/**
 * Drop-in read side for `useExperienceTemplatesByOrgId` used by the NEW_UX
 * overview grid. One-shot (not a live listener) — see the module note — but the
 * 4th tuple element is a `reload()` the grid calls after a mutation
 * (Stop/Archive/Duplicate) so the list reflects the change without a full page
 * reload. A reload keeps the currently-shown data on screen (no spinner flash);
 * only the first load for an org shows the spinner.
 */
export const useExperienceTemplatesMinimalByOrgId = (
  orgId: string
): LoadingValueExtended<Doc<IExperienceTemplate>[], any, () => void> => {
  const [state, setState] = useState<LoadingValue<Doc<IExperienceTemplate>[]>>([
    undefined,
    true,
    undefined
  ]);
  const [nonce, setNonce] = useState(0);
  const orgRef = useRef(orgId);
  useEffect(() => {
    let cancelled = false;
    // Switching org blanks to the spinner; a reload (nonce bump) keeps prior data.
    if (orgRef.current !== orgId) {
      orgRef.current = orgId;
      setState([undefined, true, undefined]);
    }
    fetchExperienceTemplatesMinimalByOrgId(orgId)
      .then((docs) => {
        if (!cancelled) setState([docs, false, undefined]);
      })
      .catch((err) => {
        // Surface the failure but don't wedge the page in a permanent spinner.
        // eslint-disable-next-line no-console
        console.error('templateMinimal runQuery failed', err);
        if (!cancelled) setState((prev) => [prev[0], false, err]);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return [state[0], state[1], state[2], reload];
};
